"use client";

import React from "react";
import { ShieldCheck, Search, FileText, ChevronRight, Zap, CircleCheck as CheckCircle2, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keyCapabilities = [{
  title: "Unknown Substance ID",
  description: "Rapid Raman and FTIR field identification for unknown powders, liquids, and solids."
}, {
  title: "Trace Narcotics & Precursors",
  description: "High-sensitivity detection algorithms for synthetic opioids, stimulants, and analogs."
}, {
  title: "Non-Destructive Sampling",
  description: "Analyze through transparent packaging without direct exposure or sample alteration."
}];
const complianceMetrics = [{
  label: "Spectral Library Entries",
  value: "30,000+"
}, {
  label: "Identification Time",
  value: "< 30s"
}, {
  label: "Deployment Standards",
  value: "MIL-STD-810H"
}];
export default function ChemicalIdentification() {
  return <section id="chemicalidentificationhero" data-nav="light" className="relative w-full bg-background text-foreground py-16 md:py-24 overflow-hidden border-b border-border">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 bg-radial-[circle_at_top_right] from-muted/50 via-transparent to-transparent pointer-events-none" />

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Core Narrative & Actions */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            {/* Breadcrumb Navigation */}
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground flex-wrap">
                <li>
                  <Link to="/HomePage" className="hover:text-foreground transition-colors">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true">
                  <ChevronRight className="size-3.5 text-muted-foreground/70" />
                </li>
                <li>
                  <Link to="/IndustriesPage" className="hover:text-foreground transition-colors">
                    Industries
                  </Link>
                </li>
                <li aria-hidden="true">
                  <ChevronRight className="size-3.5 text-muted-foreground/70" />
                </li>
                <li aria-current="page">
                  <span className="text-foreground font-medium">Law Enforcement &amp; Public Safety</span>
                </li>
              </ol>
            </nav>

            <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1 font-medium border border-border">
              <ShieldCheck className="size-3.5 text-primary" />
              <span>Public Safety &amp; Law Enforcement Solutions</span>
            </Badge>

            <h1 className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-tight md:leading-tight mb-6">
              Field Chemical &amp; Narcotics Identification Technology
            </h1>

            <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed mb-8 max-w-2xl">
              Equipping defense, border agencies, and first responders across Ireland and the UK with portable analytical tools for decisive, on-scene threat characterization and evidentiary documentation.
            </p>

            {/* Action Group */}
            <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto mb-10">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/ContactPage">
                  Request Equipment Demonstration
                  <ChevronRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/SafetyAndSecurityPage">
                  Explore Security Systems
                </Link>
              </Button>
            </div>

            <Separator className="my-2 bg-border max-w-xl" />

            {/* Metrics Row */}
            <div className="grid grid-cols-3 gap-4 sm:gap-8 pt-6 w-full max-w-xl">
              {complianceMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="flex flex-col">
                  <span className="font-default font-bold text-xl sm:text-2xl text-foreground">
                    {metric.value}
                  </span>
                  <span className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                    {metric.label}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Right Column: Visual Showcase & Operational Features */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <Card className="border border-border shadow-md overflow-hidden bg-card">
              <div className="relative aspect-4/3 w-full bg-muted overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/field_chemical_identification_device.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/field_chemical_identification_device.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/field_chemical_identification_device.png-wvc-sizes" alt="Handheld analytical analyzer being operated in field conditions" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute top-3 right-3">
                  <Badge variant="default" className="bg-primary/95 text-primary-foreground font-semibold shadow-xs">
                    Field Ready
                  </Badge>
                </div>
              </div>

              <CardContent className="p-6">
                <h2 className="font-default text-lg font-semibold text-card-foreground mb-4 flex items-center gap-2">
                  <Layers className="size-4 text-primary" />
                  Tactical Operational Capabilities
                </h2>

                <div className="space-y-4">
                  {keyCapabilities.map((item, i) => <div key={item.title} data-index={i} className="flex items-start gap-3">
                      <div className="p-1 rounded bg-secondary text-primary shrink-0 mt-0.5">
                        <CheckCircle2 className="size-4" />
                      </div>
                      <div>
                        <h3 className="font-default text-sm font-semibold text-card-foreground">
                          {item.title}
                        </h3>
                        <p className="font-sans text-xs sm:text-sm text-muted-foreground mt-0.5">
                          {item.description}
                        </p>
                      </div>
                    </div>)}
                </div>

                <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Zap className="size-3.5 text-primary" /> Direct container scan
                  </span>
                  <span className="flex items-center gap-1.5">
                    <FileText className="size-3.5 text-primary" /> Automated reporting
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}